import React, { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  FormControl,
  FormLabel,
  useColorModeValue,
  Divider,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { FiMapPin, FiSearch } from 'react-icons/fi';
import { APIProvider, Map, AdvancedMarker, useAdvancedMarkerRef } from "@vis.gl/react-google-maps";

export interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
}

interface LocationPickerProps {
  value?: LocationData;
  onChange: (location: LocationData) => void;
  placeholder?: string;
  height?: string;
  isDisabled?: boolean;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "";

const DEFAULT_CENTER = { lat: 40.7128, lng: -74.0060 }; // New York City
const DEFAULT_ZOOM = 10;

const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  placeholder = "Enter location or click on map",
  height = "300px",
  isDisabled = false,
}) => {
  const [mapCenter, setMapCenter] = useState(value ? { lat: value.latitude, lng: value.longitude } : DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(value ? 15 : DEFAULT_ZOOM);
  const [searchQuery, setSearchQuery] = useState(value?.address || '');
  const [markerRef] = useAdvancedMarkerRef();

  const borderColor = useColorModeValue("gray.200", "gray.600");

  const handleMapClick = useCallback((event: any) => {
    if (isDisabled) return;
    
    // Handle different event structures from the maps library
    const latLng = event.latLng || event.detail?.latLng;
    if (!latLng) return;
    
    const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
    const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
    
    const newLocation: LocationData = {
      latitude: lat,
      longitude: lng,
    };
    
    // Update map center to clicked location
    setMapCenter({ lat, lng });
    
    // Reverse geocoding to get address
    if (typeof window !== 'undefined' && window.google?.maps?.Geocoder) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat, lng } },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              newLocation.address = results[0].formatted_address;
              setSearchQuery(newLocation.address);
            }
            onChange(newLocation);
          }
        );
      } catch (error) {
        console.error('Geocoding error:', error);
        onChange(newLocation);
      }
    } else {
      onChange(newLocation);
    }
  }, [onChange, isDisabled]);

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() || isDisabled) return;

    if (typeof window !== 'undefined' && window.google?.maps?.Geocoder) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { address: searchQuery },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const location = results[0].geometry.location;
              const lat = location.lat();
              const lng = location.lng();
              
              const newLocation: LocationData = {
                latitude: lat,
                longitude: lng,
                address: results[0].formatted_address,
              };
              
              onChange(newLocation);
              setMapCenter({ lat, lng });
              setMapZoom(15);
              setSearchQuery(results[0].formatted_address);
            } else {
              console.warn('Geocoding failed:', status);
            }
          }
        );
      } catch (error) {
        console.error('Search error:', error);
      }
    }
  }, [searchQuery, onChange, isDisabled]);

  const handleGetCurrentLocation = useCallback(() => {
    if (isDisabled || !navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        const newLocation: LocationData = {
          latitude: lat,
          longitude: lng,
        };
        
        // Reverse geocoding
        if (typeof window !== 'undefined' && window.google?.maps?.Geocoder) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode(
              { location: { lat, lng } },
              (results, status) => {
                if (status === 'OK' && results && results[0]) {
                  newLocation.address = results[0].formatted_address;
                  setSearchQuery(newLocation.address);
                }
                onChange(newLocation);
              }
            );
          } catch (error) {
            console.error('Reverse geocoding error:', error);
            onChange(newLocation);
          }
        } else {
          onChange(newLocation);
        }
        
        setMapCenter({ lat, lng });
        setMapZoom(15);
      },
      (error) => {
        console.error('Error getting current location:', error);
      }
    );
  }, [onChange, isDisabled]);

  // Fallback component for when Google Maps is not available
  const FallbackLocationPicker = () => (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel fontSize="sm">Location (Manual Entry)</FormLabel>
        <VStack spacing={3}>
          <Input
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            isDisabled={isDisabled}
          />
          <HStack width="100%">
            <Input
              placeholder="Latitude (e.g., 40.7128)"
              value={value?.latitude?.toString() || ''}
              onChange={(e) => {
                const lat = parseFloat(e.target.value);
                if (!isNaN(lat)) {
                  onChange({
                    latitude: lat,
                    longitude: value?.longitude || 0,
                    address: searchQuery || undefined
                  });
                }
              }}
              isDisabled={isDisabled}
              size="sm"
            />
            <Input
              placeholder="Longitude (e.g., -74.0060)"
              value={value?.longitude?.toString() || ''}
              onChange={(e) => {
                const lng = parseFloat(e.target.value);
                if (!isNaN(lng)) {
                  onChange({
                    latitude: value?.latitude || 0,
                    longitude: lng,
                    address: searchQuery || undefined
                  });
                }
              }}
              isDisabled={isDisabled}
              size="sm"
            />
          </HStack>
        </VStack>
      </FormControl>
      
      {value && (value.latitude !== 0 || value.longitude !== 0) && (
        <Box p={3} bg={useColorModeValue("gray.50", "gray.700")} borderRadius="md">
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="medium">
              Selected Location:
            </Text>
            <Text fontSize="sm" color="gray.600">
              {value.address || `${value.latitude.toFixed(6)}, ${value.longitude.toFixed(6)}`}
            </Text>
          </VStack>
        </Box>
      )}
      
      <Box p={3} bg="yellow.50" borderRadius="md" borderLeftWidth="4px" borderLeftColor="yellow.400">
        <Text fontSize="xs" color="yellow.800">
          Google Maps is not available. Please enter coordinates manually or configure VITE_GOOGLE_MAPS_API_KEY.
        </Text>
      </Box>
    </VStack>
  );

  if (!GOOGLE_MAPS_API_KEY || !GOOGLE_MAPS_MAP_ID || GOOGLE_MAPS_API_KEY.includes('1234')) {
    return <FallbackLocationPicker />;
  }


  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel fontSize="sm">Location Search</FormLabel>
          <Text fontSize="xs" color="gray.600" mb={3}>
            Search for a location or click on the map to select coordinates
          </Text>
          <HStack>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder={placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                isDisabled={isDisabled}
              />
            </InputGroup>
            <Button
              size="md"
              onClick={handleSearch}
              isDisabled={!searchQuery.trim() || isDisabled}
              leftIcon={<FiSearch />}
            >
              Search
            </Button>
            <Button
              size="md"
              onClick={handleGetCurrentLocation}
              isDisabled={isDisabled}
              leftIcon={<FiMapPin />}
              variant="outline"
            >
              Current
            </Button>
          </HStack>
        </FormControl>

        <Divider />
        
        <Text fontSize="xs" color="gray.500" textAlign="center">
          Click on the map to pin a location • Drag to pan • Scroll to zoom
        </Text>

        <Box
          height={height}
          borderWidth="1px"
          borderRadius="md"
          overflow="hidden"
          borderColor={borderColor}
          cursor="crosshair"
          position="relative"
        >
          <Map
            mapId={GOOGLE_MAPS_MAP_ID}
            center={mapCenter}
            defaultZoom={mapZoom}
            gestureHandling="cooperative"
            disableDefaultUI={false}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={true}
            zoomControl={true}
            onClick={handleMapClick}
            scrollwheel={true}
            disableDoubleClickZoom={false}
            rotateControl={false}
            clickableIcons={true}
            keyboardShortcuts={true}
            style={{ width: '100%', height: '100%' }}
          >
            {value && (
              <AdvancedMarker
                ref={markerRef}
                position={{ lat: value.latitude, lng: value.longitude }}
              >
                <div style={{
                  width: 24,
                  height: 24,
                  backgroundColor: '#4285f4',
                  borderRadius: '50%',
                  border: '4px solid white',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                }} 
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                />
              </AdvancedMarker>
            )}
          </Map>
        </Box>

        {value && (
          <Box p={3} bg={useColorModeValue("gray.50", "gray.700")} borderRadius="md">
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="medium">
                Selected Location:
              </Text>
              <Text fontSize="sm" color="gray.600">
                {value.address || `${value.latitude.toFixed(6)}, ${value.longitude.toFixed(6)}`}
              </Text>
            </VStack>
          </Box>
        )}
      </VStack>
    </APIProvider>
  );
};

export default LocationPicker;